import axios from 'axios'
import { reasonForGatewayMessage } from '@/auth/logoutReason'
import {
  checkSession,
  endSession,
  noteActivity,
  readSession,
} from '@/auth/session'

declare module 'axios' {
  export interface AxiosRequestConfig {
    skipAuthRedirect?: boolean
  }
}

// Global base URL so relative-URL calls (e.g. axios.get('/api/...')) hit the
// backend instead of the Vite dev-server origin. Existing helpers that prepend
// `${API_BASE_URL}` continue to work — axios ignores baseURL when the URL is absolute.
axios.defaults.baseURL = import.meta.env.VITE_API_URL || 'http://localhost:8081'

const isAuthEndpoint = (url: string | undefined) =>
  Boolean(url?.includes('/api/auth'))

const sentToken = (headers: unknown) => {
  const value = (headers as Record<string, unknown> | undefined)?.Authorization
  return typeof value === 'string' && value.startsWith('Bearer ')
    ? value.slice(7)
    : undefined
}

// The page is on its way to /login: the caller gets no answer, so it shows no error for this request.
const silenced = () => new Promise<never>(() => {})

axios.interceptors.request.use(config => {
  if (isAuthEndpoint(config.url)) return config
  if (
    readSession().status === 'active' &&
    !config.skipAuthRedirect &&
    !checkSession()
  )
    return silenced()
  const token = localStorage.getItem('authToken')
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

axios.interceptors.response.use(
  response => {
    if (!isAuthEndpoint(response.config.url))
      noteActivity(sentToken(response.config.headers))
    return response
  },
  error => {
    const config = error.config
    const status = error.response?.status
    if (!config || !status || isAuthEndpoint(config.url))
      return Promise.reject(error)
    // Any answer but these means the Gateway accepted the token and restarted its idle clock.
    if (status !== 401 && status !== 503) {
      noteActivity(sentToken(config.headers))
      return Promise.reject(error)
    }
    if (status === 503 || config.skipAuthRedirect) return Promise.reject(error)

    const session = readSession()
    const sent = sentToken(config.headers)
    // A newer token was stored while this request carried an older one: a password change renewed it, or another tab stored it.
    if (session.status === 'active' && sent && session.token !== sent) {
      return Promise.reject(error)
    }
    endSession(reasonForGatewayMessage(error.response.data?.message))
    return silenced()
  }
)

export default axios
